import React, { useState, useEffect, Suspense, lazy } from 'react'
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom'
import MainLayout from '../layouts/MainLayout'
import AuthGuard from '../components/auth/AuthGuard'
import { useAuth } from '../context/AuthContext'
import { scanForGames } from '../services/gameDetectionService'

// Lazy-loaded pages — each becomes its own chunk so the initial bundle stays small.
const MarketingLandingPage = lazy(() => import('../pages/MarketingLandingPage'))
const LibraryPage = lazy(() => import('../pages/LibraryPage'))
const DiscoverPage = lazy(() => import('../pages/DiscoverPage'))
const ModMenuPage = lazy(() => import('../pages/ModMenuPage'))
const ActiveModsPage = lazy(() => import('../pages/ActiveModsPage'))
const FavoritesPage = lazy(() => import('../pages/FavoritesPage'))
const SettingsPage = lazy(() => import('../pages/SettingsPage'))
const AdminDashboardPage = lazy(() => import('../pages/AdminDashboardPage'))
const LogsPage = lazy(() => import('../pages/LogsPage'))
const MobileRemotePage = lazy(() => import('../pages/MobileRemotePage'))
const LoginPage = lazy(() => import('../pages/Auth/LoginPage'))
const RegisterPage = lazy(() => import('../pages/Auth/RegisterPage'))

const PageFallback = () => (
    <div className="flex items-center justify-center min-h-[50vh] w-full">
        <div className="h-10 w-10 border-2 border-white/20 border-t-white/80 rounded-full animate-spin" />
    </div>
)

const AppRoutes = () => {
    const navigate = useNavigate();
    const { isLoggedIn } = useAuth();
    
    // Initialize state from localStorage if available
    const [selectedGame, setSelectedGame] = useState(() => {
        const saved = localStorage.getItem('modhub_selected_game');
        return saved ? saved : null;
    });
    
    const [detectedGames, setDetectedGames] = useState(() => {
        const saved = localStorage.getItem('modhub_detected_games');
        return saved ? JSON.parse(saved) : [];
    });
    
    const [isScanning, setIsScanning] = useState(false)
    const [scanError, setScanError] = useState(null)
    const [searchTerm, setSearchTerm] = useState(() => {
        const saved = localStorage.getItem('modhub_search_term');
        return saved ? saved : '';
    });
    
    // Global running games tracking
    const [runningGames, setRunningGames] = useState([]);

    // Poll for running games
    useEffect(() => {
        let isMounted = true;
        const pollRunningGames = async () => {
            if (!isMounted) return;
            try {
                const apiUrl = import.meta.env.VITE_CLOUD_API_URL || '/api';
                const res = await fetch(`${apiUrl}/launcher/running`);
                if (res.ok) {
                    const data = await res.json();
                    if (data.success && isMounted) {
                        setRunningGames(data.games || []);
                    }
                }
            } catch (error) {
                // Silent fail for polling
            }
            if (isMounted) {
                setTimeout(pollRunningGames, 5000);
            }
        };
        pollRunningGames();
        return () => { isMounted = false; };
    }, []);

    // Save searchTerm to localStorage when it changes
    useEffect(() => {
        localStorage.setItem('modhub_search_term', searchTerm);
    }, [searchTerm]);

    // Save to localStorage when state changes
    useEffect(() => {
        if (selectedGame) {
            localStorage.setItem('modhub_selected_game', selectedGame);
        } else {
            localStorage.removeItem('modhub_selected_game');
        }
    }, [selectedGame]);

    useEffect(() => {
        if (detectedGames && detectedGames.length > 0) {
            localStorage.setItem('modhub_detected_games', JSON.stringify(detectedGames));
        }
    }, [detectedGames]);

    // Automatic scan on mount but only if empty (persistent cache)
    useEffect(() => {
        if (!detectedGames || detectedGames.length === 0) {
            handleScanGames()
        }
    }, [])

    const handleScanGames = async () => {
        setIsScanning(true)
        setScanError(null)

        try {
            const result = await scanForGames()
            if (result.success) {
                setDetectedGames(result.games)
            }
        } catch (error) {
            console.error('Scan failed:', error)
            setScanError(error.message)
        } finally {
            setIsScanning(false)
        }
    }

    const handleGameSelect = (game) => {
        setSelectedGame(game);
        navigate('/app/mods');
    };

    return (
        <Suspense fallback={<PageFallback />}>
        <Routes>
            {/* Public Marketing/Download Route */}
            <Route path="/" element={isLoggedIn ? <Navigate to="/app" replace /> : <MarketingLandingPage />} />

            {/* Public Auth Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/remote/:id" element={<MobileRemotePage />} />

            {/* Protected App Routes */}
            <Route path="/app" element={
                 <AuthGuard>
                     <MainLayout 
                         searchTerm={searchTerm} 
                         setSearchTerm={setSearchTerm} 
                         runningGames={runningGames}
                     />
                 </AuthGuard>
            }>
                <Route index element={<DiscoverPage searchTerm={searchTerm} onGameSelect={handleGameSelect} />} />
                <Route path="library" element={
                    <LibraryPage
                        onGameSelect={handleGameSelect}
                        detectedGames={detectedGames}
                        runningGames={runningGames}
                        isScanning={isScanning}
                        scanError={scanError}
                        onRescan={handleScanGames}
                        searchTerm={searchTerm}
                    />
                } />
                <Route path="mods" element={
                    <ModMenuPage
                        gameId={selectedGame}
                        detectedGames={detectedGames}
                        onBack={() => navigate('/library')}
                    />
                } />
                <Route path="active-mods" element={<ActiveModsPage />} />
                <Route path="favorites" element={<FavoritesPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="admin" element={<AdminDashboardPage />} />
                <Route path="admin/logs" element={<LogsPage />} />
            </Route>
            
            {/* Standalone Mobile Routes */}
            <Route path="/remote/:id" element={<MobileRemotePage />} />

            {/* Catch-all Route for unknown URLS (like /defaultsite from IONOS) */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
    )
}

export default AppRoutes;
